import { useState } from 'react';
import { TrendingUp, DollarSign, Users, Star, Cog, MessageSquare, Eye } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { DashboardModal } from '../DashboardModal';
import { formatCurrency } from '../../../../utils/currencyFormatter';
import { formatCompactNumber } from '../../../../utils/numberFormatter';
import type { Product } from '../../../../types/tycoon';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  onAction: (productId: string, action: string) => void;
  isPlayerProduct?: boolean;
  companyName?: string;
  gameDate?: { day: number; month: number; year: number };
  getPhaseProgress?: (product: Product, currentDate: { day: number; month: number; year: number }) => { progress: number; daysRemaining: number; totalDays: number };
}

export function ProductDetailModal({ isOpen, onClose, product, onAction, isPlayerProduct = true, companyName, gameDate, getPhaseProgress }: ProductDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'reviews' | 'settings'>('overview');

  if (!product) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'concept': return 'text-theme-accent bg-theme-accent/20';
      case 'design': return 'text-theme-accent bg-theme-accent/20';
      case 'development': return 'text-theme-warning bg-theme-warning/20';
      case 'manufacturing': return 'text-theme-warning bg-theme-warning/20';
      case 'production': return 'text-theme-success bg-theme-success/20';
      case 'discontinued': return 'text-theme-text-secondary bg-theme-bg-tertiary';
      default: return 'text-theme-info bg-theme-info/20';
    }
  };

  // Determine which tabs should be available based on product status
  const getAvailableTabs = () => {
    const baseTabs = [
      { id: 'overview', name: 'Overview', icon: TrendingUp },
      { id: 'settings', name: 'Settings', icon: Cog }
    ];

    // Performance tab: available during development and later stages
    if (['development', 'manufacturing', 'production', 'discontinued'].includes(product.status)) {
      baseTabs.splice(1, 0, { id: 'performance', name: 'Performance', icon: Star });
    }

    // Reviews tab: only available when product is in production
    if (['production', 'discontinued'].includes(product.status)) {
      baseTabs.splice(-1, 0, { id: 'reviews', name: 'Reviews', icon: MessageSquare });
    }

    return baseTabs;
  };

  const tabs = getAvailableTabs();

  // Reset to overview if current tab is not available for this product status
  const availableTabIds = tabs.map(tab => tab.id);
  if (!availableTabIds.includes(activeTab)) {
    setActiveTab('overview');
  }

  // Check if minimum phase duration has been met for production button
  const canStartProduction = () => {
    if (!gameDate || !getPhaseProgress || product.status !== 'manufacturing') {
      return false;
    }
    
    const phaseProgress = getPhaseProgress(product, gameDate);
    // Require at least 50% of the phase duration to be completed before allowing production
    return phaseProgress.progress >= 50;
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-theme-bg-secondary rounded-lg p-4">
          <div className="text-sm text-theme-text-secondary">Price</div>
          <div className="text-xl font-bold text-theme-success">{formatCurrency(product.price)}</div>
        </div>
        <div className="bg-theme-bg-secondary rounded-lg p-4">
          <div className="text-sm text-theme-text-secondary">Tech Score</div>
          <div className="text-xl font-bold">{Math.round(product.techScore)}</div>
        </div>
        <div className="bg-theme-bg-secondary rounded-lg p-4">
          <div className="text-sm text-theme-text-secondary">Market Appeal</div>
          <div className="text-xl font-bold">{Math.round(product.marketAppeal)}</div>
        </div>
        <div className="bg-theme-bg-secondary rounded-lg p-4">
          <div className="text-sm text-theme-text-secondary">Status</div>
          <div className={`text-sm font-medium px-2 py-1 rounded capitalize ${getStatusColor(product.status)}`}>
            {product.status}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-theme-text-primary mb-3">Description</h3>
        <p className="text-theme-text-secondary">{product.description}</p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-theme-text-primary mb-3">Financial Summary</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-theme-text-secondary">Development Cost</div>
            <div className="font-semibold">
              {isPlayerProduct ? formatCurrency(product.developmentCost) : 'Unknown'}
            </div>
          </div>
          <div>
            <div className="text-sm text-theme-text-secondary">Production Cost</div>
            <div className="font-semibold">
              {isPlayerProduct ? formatCurrency(product.productionCost) : 'Unknown'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPerformance = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-theme-info/10 rounded-lg p-4">
          <div className="flex items-center gap-2 text-theme-info mb-2">
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Units Sold</span>
          </div>
          <div className="text-2xl font-bold text-theme-info">
            {formatCompactNumber(product.salesMetrics?.unitsSold || 0)}
          </div>
        </div>
        <div className="bg-theme-success/10 rounded-lg p-4">
          <div className="flex items-center gap-2 text-theme-success mb-2">
            <DollarSign className="w-4 h-4" />
            <span className="text-sm font-medium">Revenue</span>
          </div>
          <div className="text-2xl font-bold text-theme-success">
            {formatCurrency(product.salesMetrics?.revenue || 0)}
          </div>
        </div>
        <div className="bg-theme-warning/10 rounded-lg p-4">
          <div className="flex items-center gap-2 text-theme-warning mb-2">
            <Star className="w-4 h-4" />
            <span className="text-sm font-medium">Rating</span>
          </div>
          <div className="text-2xl font-bold text-theme-warning">
            {(product.salesMetrics?.averageRating || 0).toFixed(1)}
          </div>
        </div>
        <div className="bg-theme-accent/10 rounded-lg p-4">
          <div className="flex items-center gap-2 text-theme-accent mb-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">Market Share</span>
          </div>
          <div className="text-2xl font-bold text-theme-accent">
            {((product.salesMetrics?.marketShare || 0) * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-theme-text-primary mb-3">Sales Performance</h3>
        <div className="h-64">
          <Line 
            data={{
              labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
              datasets: [{
                label: 'Units Sold',
                data: [
                  Math.max(0, (product.salesMetrics?.unitsSold || 0) * 0.1),
                  Math.max(0, (product.salesMetrics?.unitsSold || 0) * 0.3),
                  Math.max(0, (product.salesMetrics?.unitsSold || 0) * 0.6),
                  Math.max(0, (product.salesMetrics?.unitsSold || 0) * 0.8),
                  Math.max(0, (product.salesMetrics?.unitsSold || 0) * 0.9),
                  Math.max(0, (product.salesMetrics?.unitsSold || 0))
                ],
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                tension: 0.4,
                fill: true
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'top' as const,
                },
                title: {
                  display: false
                }
              },
              scales: {
                y: {
                  beginAtZero: true
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderReviews = () => (
    <div className="space-y-6">
      {/* Professional/Industry Reviews Section */}
      <div className="bg-theme-info/10 rounded-lg p-4 border border-theme-info/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-theme-text-primary">Professional Reviews</h3>
          <div className="text-sm text-theme-info">
            Industry Experts & Publications
          </div>
        </div>

        <div className="space-y-4">
          {product.reviews?.professional.length > 0 ? (
            product.reviews.professional.map((review, index) => (
              <div key={index} className="border-b border-theme-info/30 pb-3 last:border-b-0">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex text-theme-info">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${
                        i < review.rating ? 'fill-current' : 'text-theme-text-muted'
                      }`} />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-theme-text-primary">{review.publication}</span>
                  <span className="text-xs text-theme-text-secondary">• {review.reviewerTitle}</span>
                </div>
                <p className="text-sm text-theme-text-secondary">{review.text}</p>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-theme-text-secondary">
              <p className="text-sm">No professional reviews available yet.</p>
              <p className="text-xs">Reviews will appear after product release.</p>
            </div>
          )}
        </div>
      </div>

      {/* Customer Reviews Section */}
      <div className="bg-theme-bg-secondary rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-theme-text-primary">Customer Reviews</h3>
          <div className="text-sm text-theme-text-muted">
            Average: {(product.salesMetrics?.averageRating || 0).toFixed(1)}/5.0
          </div>
        </div>
        
        <div className="space-y-4">
          {product.reviews?.customer.length > 0 ? (
            product.reviews.customer.map((review, index) => (
              <div key={index} className="border-b border-theme-border pb-3 last:border-b-0">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex text-theme-warning">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${
                        i < review.rating ? 'fill-current' : 'text-theme-text-muted'
                      }`} />
                    ))}
                  </div>
                  <span className="text-sm font-medium">{review.author}</span>
                </div>
                <p className="text-sm text-theme-text-secondary">{review.text}</p>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-theme-text-muted">
              <p className="text-sm">No customer reviews available yet.</p>
              <p className="text-xs">Reviews will appear after product release.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      {isPlayerProduct && (
        <div>
          <h3 className="text-lg font-semibold text-theme-text-primary mb-3">Product Actions</h3>
          <div className="space-y-3">
            {product.status === 'manufacturing' && (
              <div>
                <button
                  onClick={() => {
                    onAction(product.id, 'produce');
                    onClose();
                  }}
                  disabled={!canStartProduction()}
                  className={`w-full py-2 px-4 rounded transition-colors ${
                    canStartProduction()
                      ? 'bg-theme-success text-white hover:bg-theme-success/80'
                      : 'bg-theme-bg-tertiary text-theme-text-muted cursor-not-allowed'
                  }`}
                >
                  Start Production
                </button>
                {!canStartProduction() && gameDate && getPhaseProgress && (
                  <p className="text-xs text-theme-text-muted mt-1">
                    Manufacturing phase must reach 50% completion before production can start.
                    {(() => {
                      const progress = getPhaseProgress(product, gameDate);
                      return ` Current progress: ${Math.round(progress.progress)}% (${progress.daysRemaining} days remaining)`;
                    })()}
                  </p>
                )}
              </div>
            )}
            
            {product.status === 'production' && (
              <button
                onClick={() => {
                  onAction(product.id, 'discontinue');
                  onClose();
                }}
                className="w-full bg-theme-error text-white py-2 px-4 rounded hover:bg-theme-error/80 transition-colors"
              >
                Discontinue Product
              </button>
            )}

            <button
              onClick={() => {
                onAction(product.id, product.status === 'discontinued' ? 'view-readonly' : 'edit');
                onClose();
              }}
              className="w-full bg-theme-info text-white py-2 px-4 rounded hover:bg-theme-info/80 transition-colors"
            >
              {product.status === 'discontinued' ? 'View in Designer' : 'Edit in Designer'}
            </button>
          </div>
        </div>
      )}
      
      {!isPlayerProduct && (
        <div>
          <h3 className="text-lg font-semibold text-theme-text-primary mb-3">Product Actions</h3>
          <div className="space-y-3">
            <button
              onClick={() => {
                onAction(product.id, 'view-readonly');
                onClose();
              }}
              className="w-full bg-theme-bg-tertiary text-white py-2 px-4 rounded hover:bg-theme-border transition-colors flex items-center justify-center gap-2"
            >
              <Eye className="w-4 h-4" />
              View in Designer
            </button>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold text-theme-text-primary mb-3">Metadata</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-theme-text-secondary">Company:</span>
            <span>{companyName || product.metadata?.companyId || 'Unknown'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-theme-text-secondary">Release Date:</span>
            <span>
              {product.metadata?.releaseDate 
                ? `${product.metadata.releaseDate.month}/${product.metadata.releaseDate.day}/${product.metadata.releaseDate.year}`
                : product.status === 'development' ? 'Not Released' : 'N/A'
              }
            </span>
          </div>
          {product.status === 'discontinued' && (
            <div className="flex justify-between">
              <span className="text-theme-text-secondary">Discontinued Date:</span>
              <span>
                {product.metadata?.discontinuedDate 
                  ? `${product.metadata.discontinuedDate.month}/${product.metadata.discontinuedDate.day}/${product.metadata.discontinuedDate.year}`
                  : 'N/A'
                }
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={onClose}
      title={product.name}
      size="lg"
    >
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-theme-bg-tertiary rounded-lg p-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'overview' | 'performance' | 'reviews' | 'settings')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-theme-bg-content text-theme-accent shadow-sm'
                    : 'text-theme-text-secondary hover:text-theme-text-primary'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'performance' && renderPerformance()}
        {activeTab === 'reviews' && renderReviews()}
        {activeTab === 'settings' && renderSettings()}
      </div>
    </DashboardModal>
  );
}